"use client";

import { Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createRandomId } from "@/lib/random-id";
import { MatchResult, ScoreCounter } from "../base-match-template";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const parseIntOrZero = (value: string) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const parseOptionalInt = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  if (value.trim() === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export type BasketballScoreEditorProps = {
  initialData?: Record<string, unknown> | null;
  onChange: (data: Record<string, unknown>) => void;
};

export function BasketballScoreEditor({
  initialData,
  onChange,
}: BasketballScoreEditorProps) {
  const initialMatchScore = isRecord(initialData?.matchScore)
    ? (initialData?.matchScore as Record<string, unknown>)
    : null;

  const [yourPoints, setYourPoints] = useState(
    parseOptionalInt(initialMatchScore?.yourPoints) ?? 0,
  );
  const [opponentPoints, setOpponentPoints] = useState(
    parseOptionalInt(initialMatchScore?.opponentPoints) ?? 0,
  );

  const initialPeriods = Array.isArray(initialData?.periods)
    ? (initialData?.periods as unknown[])
    : [];

  const get = (label: string, key: "yourScore" | "opponentScore") => {
    const row = initialPeriods.find(
      (p) => isRecord(p) && typeof p.label === "string" && p.label === label,
    ) as Record<string, unknown> | undefined;
    return String(parseOptionalInt(row?.[key]) ?? "");
  };

  const [showDetails, setShowDetails] = useState(initialPeriods.length > 0);

  const [quarters, setQuarters] = useState<
    Array<{ label: string; your: string; opp: string }>
  >(() => [
    {
      label: "Q1",
      your: get("Q1", "yourScore"),
      opp: get("Q1", "opponentScore"),
    },
    {
      label: "Q2",
      your: get("Q2", "yourScore"),
      opp: get("Q2", "opponentScore"),
    },
    {
      label: "Q3",
      your: get("Q3", "yourScore"),
      opp: get("Q3", "opponentScore"),
    },
    {
      label: "Q4",
      your: get("Q4", "yourScore"),
      opp: get("Q4", "opponentScore"),
    },
  ]);

  const [overtimes, setOvertimes] = useState<
    Array<{ id: string; label: string; your: string; opp: string }>
  >(() =>
    initialPeriods
      .filter(
        (p) =>
          isRecord(p) &&
          typeof p.label === "string" &&
          p.label.startsWith("OT"),
      )
      .map((p) => {
        const row = p as Record<string, unknown>;
        return {
          id: createRandomId(),
          label: row.label as string,
          your: String(parseOptionalInt(row.yourScore) ?? ""),
          opp: String(parseOptionalInt(row.opponentScore) ?? ""),
        };
      }),
  );

  const derivedTotals = useMemo(() => {
    const totals = [...quarters, ...overtimes].reduce(
      (acc, period) => ({
        your: acc.your + parseIntOrZero(period.your),
        opp: acc.opp + parseIntOrZero(period.opp),
      }),
      { your: 0, opp: 0 },
    );
    return totals;
  }, [quarters, overtimes]);

  useEffect(() => {
    if (!showDetails) return;
    setYourPoints(derivedTotals.your);
    setOpponentPoints(derivedTotals.opp);
  }, [derivedTotals, showDetails]);

  useEffect(() => {
    const periods = showDetails
      ? ([
          ...quarters.map((q) => ({
            label: q.label,
            yourScore: parseIntOrZero(q.your),
            opponentScore: parseIntOrZero(q.opp),
          })),
          ...overtimes.map((ot) => ({
            label: ot.label,
            yourScore: parseIntOrZero(ot.your),
            opponentScore: parseIntOrZero(ot.opp),
          })),
        ] satisfies Array<{
          label: string;
          yourScore: number;
          opponentScore: number;
        }>)
      : [];

    onChange({
      matchScore: { yourPoints, opponentPoints },
      periods,
    });
  }, [yourPoints, opponentPoints, showDetails, quarters, overtimes, onChange]);

  const handleAddOvertime = () => {
    const nextIndex = overtimes.length + 1;
    setOvertimes((prev) => [
      ...prev,
      { id: createRandomId(), label: `OT${nextIndex}`, your: "", opp: "" },
    ]);
  };

  const handleRemoveOvertime = () => {
    setOvertimes((prev) => prev.slice(0, -1));
  };

  const handleClearBreakdown = () => {
    setShowDetails(false);
    setQuarters((prev) => prev.map((q) => ({ ...q, your: "", opp: "" })));
    setOvertimes([]);
  };

  return (
    <div className="space-y-3">
      <Label className="text-text-primary">Final Score</Label>
      <div className="flex items-center justify-center gap-4">
        <ScoreCounter
          label="Team 1"
          value={yourPoints}
          onChange={setYourPoints}
          max={200}
          isWinner={yourPoints > opponentPoints}
          variant="brand"
          disabled={showDetails}
        />
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <ScoreCounter
          label="Team 2"
          value={opponentPoints}
          onChange={setOpponentPoints}
          max={200}
          isWinner={opponentPoints > yourPoints}
          variant="opponent"
          disabled={showDetails}
        />
      </div>
      <MatchResult yourScore={yourPoints} opponentScore={opponentPoints} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-text-primary">Breakdown (Optional)</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowDetails((prev) => !prev)}
          >
            {showDetails ? "Totals only" : "Add details"}
          </Button>
        </div>

        {showDetails && (
          <div className="space-y-3 rounded-xl border border-border bg-surface-alt/50 p-3">
            <p className="text-xs text-text-secondary">
              Enter points per quarter (and overtime). Totals are
              auto-calculated.
            </p>

            <div className="space-y-2">
              {[...quarters, ...overtimes].map((period, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static editor list, period.label is unique
                  key={`editor-period-${index}-${period.label}`}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="w-16 text-sm text-text-secondary">
                    {period.label}
                  </span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={period.your}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (index < quarters.length) {
                          setQuarters((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], your: value };
                            return next;
                          });
                          return;
                        }
                        const overtimeIndex = index - quarters.length;
                        setOvertimes((prev) => {
                          const next = [...prev];
                          if (!next[overtimeIndex]) return prev;
                          next[overtimeIndex] = {
                            ...next[overtimeIndex],
                            your: value,
                          };
                          return next;
                        });
                      }}
                      placeholder="-"
                      className="h-9 w-20 text-center"
                      min={0}
                      max={99}
                    />
                    <span className="text-text-secondary">–</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      value={period.opp}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (index < quarters.length) {
                          setQuarters((prev) => {
                            const next = [...prev];
                            next[index] = { ...next[index], opp: value };
                            return next;
                          });
                          return;
                        }
                        const overtimeIndex = index - quarters.length;
                        setOvertimes((prev) => {
                          const next = [...prev];
                          if (!next[overtimeIndex]) return prev;
                          next[overtimeIndex] = {
                            ...next[overtimeIndex],
                            opp: value,
                          };
                          return next;
                        });
                      }}
                      placeholder="-"
                      className="h-9 w-20 text-center"
                      min={0}
                      max={99}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddOvertime}
              >
                <Plus className="mr-1 h-4 w-4" />
                Add OT
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemoveOvertime}
                disabled={overtimes.length === 0}
              >
                <Trash2 className="mr-1 h-4 w-4" />
                Remove OT
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearBreakdown}
                className="text-error hover:bg-error/10 hover:text-error"
              >
                <X className="mr-1 h-4 w-4" />
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
